import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {NavPreferences, Section, SECTIONS} from 'constants/NavConstants';
import {StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {isCompactDisplay} from 'nav/NavUtil';
import ItemListing from 'nav/sidepanel/listing/ItemListing';
import preferences from 'Preferences';
import {getAvailableCustomSortables} from 'processes/dashboard/processes-search-platform/listing-renderers/getAvailableSorts';
import CreateTestOrdersButton from 'sample-tests/ui/test-orders/buttons/CreateTestOrdersButton';
import * as FilterUtil from 'search/FilterUtil';
import {FilterKey, SearchParams, SearchType, SortKey} from 'search/SearchConstants';
import withSearching, {SearchProps} from 'search/withSearching';
import {getStudyableModelTypes} from 'studies/studyable/StudyableUtils';
import {getProcedureTypeForModelType, getProcedureTypeForStudyTypeAllowNull} from 'studies/TypedStudyUtils';
import isModelTypeEnabledForListing from 'util/isModelTypeEnabledForListing';

import {getStudyItemListingSearchSessionId} from '../StudySearchHelpers';

interface StudyItemsListingProps {
  studyId: string;
  studyType: StudyType;
}

function getSelectedTypes(searchParams: SearchParams): Array<string> {
  const typeFilterValue = FilterUtil.getFilterValueByKey(
    searchParams.filters,
    FilterKey.TYPES
  ) as Array<string>;
  return typeFilterValue;
}

function isModelTypeEnabledForTypedStudyListing(type: ModelTypeConstants, studyType: StudyType): boolean {
  if (!isModelTypeEnabledForListing(type)) {
    return false;
  }
  const procedureType = getProcedureTypeForModelType(type);
  if (procedureType && procedureType !== getProcedureTypeForStudyTypeAllowNull(studyType)) {
    return false;
  }
  return true;
}

function getAvailableTypes(studyType: StudyType): Array<string> {
  return getStudyableModelTypes().filter((type) => isModelTypeEnabledForTypedStudyListing(type, studyType));
}

/**
 * Component to show all items in a Study. This component was developed to be rendered inside a workspace item
 * buffer tab (rather than in the side panel) and as such is a bit different from other listings. It doesn't
 * support saving the current params in the URL or saved searches, and should always have the `IN_STUDY`
 * filter pinned to the workspace tab's Study.
 *
 * If we do decide to add URL support or saved searches, we should update the IN_STUDY filter to support
 * multiple studies since it'll be harder to make that change once the filter state is saved in URLs or saved
 * searches.
 */
export function StudyItemsListing(props: StudyItemsListingProps & SearchProps): JSX.Element {
  const [_, setIsCompactDisplay] = useState<boolean>(isCompactDisplay());

  const _setIsCompactDisplay = (isCompact: boolean): void => {
    setIsCompactDisplay(isCompact);
    preferences.set(NavPreferences.SIDEPANEL_IS_DISPLAY_COMPACT, isCompact);
  };

  const selectedTypes = getSelectedTypes(props.searchParams);
  const customSortables = getAvailableCustomSortables(selectedTypes);

  return (
    <ItemListing
      {...props}
      isExpanded={true}
      enableToggleExpanded={false}
      activeItemId={null}
      isCompactDisplay={isCompactDisplay()}
      setIsCompactDisplay={_setIsCompactDisplay}
      actionSource={ActionOrigin.STUDY_ITEMS_PAGE}
      searchSessionId={getStudyItemListingSearchSessionId(props.studyId)}
      availableSortKeys={[
        SortKey.SORT_NAME_KEY,
        SortKey.SORT_IDENTIFIER_KEY,
        SortKey.SORT_MODIFIED_AT_KEY,
        SortKey.SORT_CREATED_AT_KEY,
      ]}
      availableTypes={getAvailableTypes(props.studyType)}
      extraPrimaryFilterOptions={[]}
      preservedFilterKeys={[FilterKey.IN_STUDY]}
      section={Section.STUDY_ITEMS}
      dataTestAttrs={{component: 'StudyItemsListing', element: 'root'}}
      disableBLAST={props.studyType !== StudyType.CUSTOM}
      showMultiKeySortMenu={customSortables.length > 0}
      customSortables={customSortables}
      renderAdditionalToolbarButtons={(items, selectedIds) => (
        <div className={css(styles.toolbar)}>
          {BenchlingConfig.SAMPLE_TESTS_ENABLE &&
            [StudyType.CLINICAL, StudyType.PRECLINICAL].includes(props.studyType) && (
              <div className={css(styles.createTestOrdersButton)}>
                <CreateTestOrdersButton
                  sampleIds={
                    items
                      ? items.filter((item) => selectedIds.includes(item.id)).map((item) => item.id)
                      : null
                  }
                  studyIds={[props.studyId]}
                />
              </div>
            )}
        </div>
      )}
    />
  );
}

// Default search params are set in StudyListingTabView before rendering
export default withSearching(StudyItemsListing, SearchType.STUDY_ITEMS, SECTIONS.STUDY_ITEMS, {
  getSearchSessionId: (initialProps: StudyItemsListingProps & SearchProps) =>
    getStudyItemListingSearchSessionId(initialProps.studyId),
});

const styles = StyleSheet.create({
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  createTestOrdersButton: {
    paddingLeft: '10px',
  },
});
